import { useState, useMemo } from 'react';
import { useGetDispatchPlaces } from '@/features/dispatch-places/dispatch-places.queries';
import { useGetAmazonCounter, useProcessPoints } from '@/features/amazon/amazon.queries';

export const useAmazonPage = () => {
  const { data: placesData, isLoading: isLoadingPlaces, isError: isErrorPlaces } = useGetDispatchPlaces();
  const { data: counter, isLoading: isLoadingCounter } = useGetAmazonCounter();
  const processMutation = useProcessPoints();

  const [selected_ids, setSelectedIds] = useState<string[]>([]);
  const [is_confirm_modal_open, setIsConfirmModalOpen] = useState(false);
  const [pending_action, setPendingAction] = useState<number | null>(null);

  const places = useMemo(() => {
    // We only care about places that have an amazon config or could have one
    return placesData?.items ?? [];
  }, [placesData]);

  const onToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const onSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(places.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const selectedPlaces = useMemo(() => {
    return places.filter((p) => selected_ids.includes(p.id));
  }, [places, selected_ids]);

  // Logic for action availability
  // Actions: Planear=1, Activar=2, Desactivar=3, Actualizar=4
  const availableActions = useMemo(() => {
    if (selectedPlaces.length === 0) return new Set<number>();

    const actions = new Set<number>([1, 2, 3, 4]);

    for (const place of selectedPlaces) {
      const status = place.amazon?.status;
      const is_processing = place.amazon?.processing_status === 1;

      if (is_processing) {
        // If any point is processing, no bulk actions allowed
        return new Set<number>();
      }

      const currentActions = new Set<number>();
      if (!status) {
        currentActions.add(1);
      } else if (status === 'PLANNED') {
        currentActions.add(2);
        currentActions.add(3);
        currentActions.add(4);
      } else if (status === 'ACTIVE') {
        currentActions.add(3);
        currentActions.add(4);
      } else if (status === 'INACTIVE') {
        currentActions.add(2);
      }

      // Intersection
      for (const a of Array.from(actions)) {
        if (!currentActions.has(a)) {
          actions.delete(a);
        }
      }
    }

    return actions;
  }, [selectedPlaces]);

  const onInitiateAction = (action: number) => {
    setPendingAction(action);
    setIsConfirmModalOpen(true);
  };

  const onConfirmAction = () => {
    if (pending_action === null) return;

    processMutation.mutate({
      dispatch_place_ids: selected_ids,
      action: pending_action
    }, {
      onSuccess: () => {
        setIsConfirmModalOpen(false);
        setPendingAction(null);
        setSelectedIds([]);
      }
    });
  };

  return {
    places,
    counter,
    isLoading: isLoadingPlaces || isLoadingCounter,
    isError: isErrorPlaces,
    selected_ids,
    available_actions: availableActions,
    is_confirm_modal_open,
    is_processing: processMutation.isPending,
    last_response: processMutation.data,
    onToggleSelect,
    onSelectAll,
    onInitiateAction,
    onConfirmAction,
    onCloseModal: () => setIsConfirmModalOpen(false),
  };
};
